import React, { useState } from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useTheme } from '@mui/material/styles';
import {
	Grid,
	CircularProgress,
	Button,
	Stack,
	TextField,
	Box,
	Typography,
	IconButton,
	FormGroup,
	FormControl,
	FormControlLabel,
	InputLabel,
	Switch,
	Select,
	MenuItem
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CloseIcon from '@mui/icons-material/Close';
import utils from 'components/utils';
import moment from 'moment';

const WishlistCreate = ({ onClose, onNoteChange, defaultNotesValue, defaultNameValue, onNameChange, resetOnSave }) => {
	const theme = useTheme();
	const translate = utils.useI18N();
	const iCan = utils.usePermissions();
	const [iAmPersonifying, target] = utils.usePersonification();
	const permissions = useStoreState(state => state.me.permissions);
	const create = useStoreActions(dispatch => dispatch.wishlist.create.sendRequest);
	const loading = useStoreState(state => state.wishlist.create.loading);
	const [textAreaCount, settextAreaCount] = React.useState(100);
	const [special, setSpecial] = useState(false);
	const [eventDate, setEventDate] = useState(null);
	const [deliveryDate, setDeliveryDate] = useState(null);
	const [specialType, setSpecialType] = useState(null);
	const specialEnabled = iAmPersonifying && permissions && permissions['MANAGE_WISHLIST_SPECIAL'];
	const eventDateBefore = eventDate && moment(eventDate).diff(moment(), 'days') < 14;
	const eventDatePast = eventDate && moment(eventDate).diff(moment(), 'days') < 0;
	const deliveryDatePast = deliveryDate && moment(deliveryDate).diff(moment(), 'days') < 0;
	const isInvalidEventDate = eventDate && moment(eventDate).format('DD/MM/YYYY').length === 1;
	const isInvalidDeliveryDate =
		deliveryDate &&
		(moment(deliveryDate).format('DD/MM/YYYY').length === 1 ||
			moment(eventDate).diff(moment(deliveryDate), 'days') < 0);
	const isDisable =
		defaultNameValue.trim() === '' ||
		(special && (!eventDate || isInvalidEventDate || eventDatePast || isInvalidDeliveryDate || deliveryDatePast));

	const WLType = [
		{ id: 0, label: translate('wishlistSpecial.typeAdozione'), value: 'ADOZIONE' },
		{ id: 1, label: translate('wishlistSpecial.typeFestival'), value: 'FESTIVAL' },
		{ id: 2, label: translate('wishlistSpecial.typePresentazione'), value: 'PRESENTAZIONE' }
	];

	const getMinValueSpecial = wishlistType => {
		if (wishlistType === 'ADOZIONE') return 15;
		if (wishlistType === 'FESTIVAL') return 3;
		if (wishlistType === 'PRESENTAZIONE') return 10;

		return 1;
	};

	const recalculate = e => {
		const charCount = e.target.value.length;
		const charLeft = 100 - charCount;
		settextAreaCount(charLeft);
	};

	const handelSpecial = event => {
		setSpecial(event.target.checked);
		if (!specialType) setSpecialType(WLType[0].value);
	};

	const handelSpecialType = event => {
		setSpecialType(event.target.value);
	};

	const onClick = _ => {
		const convertEventDate = special && eventDate ? moment(eventDate).format('YYYY-MM-DD') : null;
		const convertDeliveryDate = special && deliveryDate ? moment(deliveryDate).format('YYYY-MM-DD') : null;
		create({
			name: defaultNameValue,
			note: defaultNotesValue,
			type: specialType,
			special: special,
			eventDate: convertEventDate,
			deliveryDate: convertDeliveryDate
		});
		resetOnSave && resetOnSave();
	};

	return (
		<Stack
			direction={'column'}
			spacing={2}
			sx={{
				width: '100%',
				maxWidth: { xs: '340px', md: '540px', sm: '540px' },
				minWidth: '340px',
				height: { xs: '360px', md: '420px', sm: '360px' },
				padding: '4px 10px',
				overflowY: 'scroll'
			}}
		>
			<Stack
				direction="row"
				justifyContent="flex-start"
				alignItems="center"
				spacing={2}
				sx={{ width: '100%', borderBottom: `1px solid ${theme.palette.primary.main}` }}
			>
				<Box sx={{ width: '90%' }}>
					<Typography variant="titleSection" sx={{ color: theme.palette.primary.main }}>
						{translate('wishlist.createWl')}
					</Typography>
				</Box>
				<Box sx={{ width: '10%' }}>
					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</Box>
			</Stack>
			<Grid container item xs={12}>
				<TextField
					value={defaultNameValue}
					onChange={onNameChange}
					fullWidth={true}
					label={translate('wishlist.name')}
					size="small"
					inputProps={{ maxLength: 80 }}
				/>
			</Grid>
			<Grid container item xs={12}>
				<TextField
					rows={5}
					maxRows={5}
					multiline
					maxLength={100}
					aria-label="warning message"
					placeholder="note"
					defaultValue={defaultNotesValue}
					sx={{ width: '100%', resize: 'none' }}
					onChange={e => {
						onNoteChange(e);
						recalculate(e);
					}}
					inputProps={{ maxLength: 100 }}
				/>
				<p style={{ marginTop: '0px' }}>Inserire al massimo {textAreaCount} caratteri</p>
			</Grid>
			{specialEnabled && (
				<Grid container item xs={12}>
					<Stack
						direction="column"
						justifyContent="flex-start"
						alignItems="flex-start"
						spacing={1}
						sx={{ width: '100%', marginTop: '-10px !important' }}
					>
						<Stack
							direction={{ xs: 'column', md: 'row', sm: 'row' }}
							justifyContent="flex-start"
							alignItems="flex-start"
							spacing={1}
							sx={{ width: '100%' }}
						>
							<FormGroup sx={{ width: { xs: '100%', md: '50%', sm: '50%' } }}>
								<FormControlLabel
									label={
										<Typography variant="text" sx={{ textTransform: 'capitalize' }}>
											{translate('wishlist.special')}
										</Typography>
									}
									control={<Switch checked={special} onChange={handelSpecial} />}
								/>
							</FormGroup>
							{special && (
								<FormControl size="small">
									<InputLabel id="wishlist-special-type-label">
										{translate('wishlistSpecial.type')}
									</InputLabel>
									<Select
										labelId="wishlist-special-type-label"
										id="wishlist-special-type"
										value={specialType}
										label={translate('wishlistSpecial.type')}
										onChange={handelSpecialType}
									>
										{WLType.map(t => (
											<MenuItem key={t.id} value={t.value}>
												{t.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}
						</Stack>
						{special && (
							<LocalizationProvider dateAdapter={AdapterMoment}>
								<Stack
									direction={{ xs: 'column', md: 'row', sm: 'row' }}
									justifyContent="flex-start"
									alignItems="flex-start"
									spacing={1}
									sx={{ width: '100%' }}
								>
									<DatePicker
										disablePast
										value={eventDate}
										format="DD/MM/YYYY"
										onChange={date => setEventDate(date)}
										label={translate('wishlist.eventDate')}
										slotProps={{
											textField: {
												size: 'small',
												helperText:
													isInvalidEventDate || eventDatePast ? (
														<Typography
															variant="text"
															sx={{ fontSize: '12px', color: theme.palette.custom.red }}
														>
															{translate('wishlist.eventDateError')}
														</Typography>
													) : eventDateBefore ? (
														<Typography
															variant="text"
															sx={{
																fontSize: '12px',
																color: theme.palette.custom.orange
															}}
														>
															{translate('wishlist.specialDateBefore')}
														</Typography>
													) : !eventDate ? (
														<Typography variant="text" sx={{ fontSize: '12px' }}>
															{translate('wishlist.specialDateDesc')}
														</Typography>
													) : (
														<Typography variant="text" sx={{ fontSize: '12px' }}>
															{translate('wishlist.eventDate')}
														</Typography>
													)
											}
										}}
									/>
									<DatePicker
										disablePast
										value={deliveryDate}
										format="DD/MM/YYYY"
										onChange={date => setDeliveryDate(date)}
										label={translate('wishlist.deliveryDate')}
										slotProps={{
											textField: {
												size: 'small',
												helperText:
													isInvalidDeliveryDate || deliveryDatePast ? (
														<Typography
															variant="text"
															sx={{ fontSize: '12px', color: theme.palette.custom.red }}
														>
															{translate('wishlist.deliveryDateError')}
														</Typography>
													) : !deliveryDate ? (
														<Typography variant="text" sx={{ fontSize: '12px' }}>
															{translate('wishlist.specialDateDeliveryDesc')}
														</Typography>
													) : (
														<Typography variant="text" sx={{ fontSize: '12px' }}>
															{translate('wishlist.deliveryDate')}
														</Typography>
													)
											}
										}}
									/>
								</Stack>
							</LocalizationProvider>
						)}
						{special && (
							<Stack
								direction="column"
								justifyContent="flex-start"
								alignItems="flex-start"
								spacing={0}
								sx={{ width: '100%', marginLeft: '8px !important' }}
							>
								<Typography
									variant="text"
									sx={{ fontSize: '13px', color: theme.palette.custom.orange }}
								>
									{specialType === 'PRESENTAZIONE'
										? translate('wishlistSpecial.qtaIndicationAtLeast').replace(
												'XX',
												getMinValueSpecial(specialType)
										  )
										: translate('wishlistSpecial.qtaIndicationAtAll').replace(
												'XX',
												getMinValueSpecial(specialType)
										  )}
								</Typography>
							</Stack>
						)}
					</Stack>
				</Grid>
			)}
			<Grid container item xs={12}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
					spacing={2}
					sx={{ width: '100%', marginTop: '10px !important' }}
				>
					<Button variant="dark" disabled={isDisable} onClick={onClick}>
						{loading ? (
							<CircularProgress size={24} sx={{ color: theme.palette.custom.white }} />
						) : (
							translate('common.save')
						)}
					</Button>
					<Button variant="light" onClick={onClose}>
						{translate('common.cancel')}
					</Button>
				</Stack>
			</Grid>
		</Stack>
	);
};

export default WishlistCreate;
